/* eslint-disable react/prop-types */
import React from 'react';
import Toggle from 'react-bootstrap-toggle';
import {
  TaxType,
  CreditRating,
  LeaseMileageNames,
  Origin,
} from '@upstart/harmony/lib/constants';
import { isThirdPartyVendorTaxesEnabled } from '@upstart/harmony/lib/dealer-utils';
import { get } from 'lodash';

const toggleButton = props => (
  <Toggle
    on="on"
    off="off"
    size="sm"
    className="toggle-switch"
    active={props.value === true}
    onClick={value => props.onChange(value)}
    disabled={props.disabled}
  />
);

export const step1UiSchema = {
  classNames: 'hidden-title',
  onlinePricingMatrix: {
    'ui:field': 'onlinePricingMatrix',
  },
};

export const step2UiSchema = {
  classNames: 'hidden-title',
  instorePricingMatrix: {
    'ui:field': 'instorePricingMatrix',
  },
};

const taxTypeEntryUi = disableTaxes => ({
  classNames: 'col-md-4',
  name: { classNames: 'col-md-6', 'ui:disabled': disableTaxes },
  rate: {
    'ui:field': 'percent',
    classNames: 'col-md-6',
    'ui:enumDisabled': disableTaxes,
  },
  useRate: {
    'ui:field': 'percent',
    classNames: 'col-md-6',
    'ui:enumDisabled': disableTaxes,
  },
  maxAmount: { classNames: 'col-md-6', 'ui:disabled': disableTaxes },
});

const taxUi = disableTaxes => ({
  [TaxType.STATE]: taxTypeEntryUi(disableTaxes),
  [TaxType.COUNTY]: taxTypeEntryUi(disableTaxes),
  [TaxType.CITY]: taxTypeEntryUi(disableTaxes),
});

export const getStep3UiSchema = data => {
  const disableTaxes = get(data, 'featureConfig.disableTaxes', false);
  const shouldDisableTaxByDealerAddress = !isThirdPartyVendorTaxesEnabled(data);

  return {
    'ui:widget': 'dtEntityId',
    featureConfig: {
      taxByDealerAddress: {
        'ui:disabled': shouldDisableTaxByDealerAddress,
      },
    },
    desking: {
      capitalizedTaxRate: {
        'ui:field': 'percent',
        classNames: 'col-md-3',
      },
    },
    taxes: {
      cash: taxUi(disableTaxes),
      purchase: taxUi(disableTaxes),
      lease: taxUi(disableTaxes),
    },
    addOns: {
      New: {
        items: {
          name: { classNames: 'col-md-4' },
          price: { classNames: 'col-md-2' },
          cost: { classNames: 'col-md-2' },
          code: { classNames: 'col-md-2' },
          taxableForLease: {
            classNames: 'col-md-2 taxable-checkbox-container',
          },
          dtEntityId: {
            'ui:widget': 'dtEntityId',
            classNames: 'col-md-2',
          },
        },
      },
      CPO: {
        items: {
          name: { classNames: 'col-md-4' },
          price: { classNames: 'col-md-2' },
          cost: { classNames: 'col-md-2' },
          code: { classNames: 'col-md-2' },
          taxableForLease: {
            classNames: 'col-md-2 taxable-checkbox-container',
          },
          dtEntityId: {
            'ui:widget': 'dtEntityId',
            classNames: 'col-md-2',
          },
        },
      },
      Used: {
        items: {
          name: { classNames: 'col-md-4' },
          price: { classNames: 'col-md-2' },
          cost: { classNames: 'col-md-2' },
          code: { classNames: 'col-md-2' },
          taxableForLease: {
            classNames: 'col-md-2 taxable-checkbox-container',
          },
          dtEntityId: {
            'ui:widget': 'dtEntityId',
            classNames: 'col-md-2',
          },
        },
      },
    },
  };
};

const getLeaseCreditRatingUiSchema = (term, mileages) => ({
  moneyFactor: {
    [LeaseMileageNames.MILEAGE1]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term,
    },
    [LeaseMileageNames.MILEAGE2]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term,
    },
    [LeaseMileageNames.MILEAGE3]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term,
    },
    [LeaseMileageNames.MILEAGE4]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term || !mileages?.[LeaseMileageNames.MILEAGE4],
    },
    [LeaseMileageNames.MILEAGE5]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term || !mileages?.[LeaseMileageNames.MILEAGE5],
    },
  },
  onePayLeaseMoneyFactor: {
    [LeaseMileageNames.MILEAGE1]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term,
    },
    [LeaseMileageNames.MILEAGE2]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term,
    },
    [LeaseMileageNames.MILEAGE3]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term,
    },
    [LeaseMileageNames.MILEAGE4]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term || !mileages?.[LeaseMileageNames.MILEAGE4],
    },
    [LeaseMileageNames.MILEAGE5]: {
      classNames: 'col-md-5ths',
      'ui:disabled': !term || !mileages?.[LeaseMileageNames.MILEAGE5],
    },
  },
});

export const getLeaseTermUiSchema = (term, formData) => ({
  credit: {
    [CreditRating.POOR]: getLeaseCreditRatingUiSchema(term, formData),
    [CreditRating.AVERAGE]: getLeaseCreditRatingUiSchema(term, formData),
    [CreditRating.GOOD]: getLeaseCreditRatingUiSchema(term, formData),
    [CreditRating.EXCELLENT]: getLeaseCreditRatingUiSchema(term, formData),
  },
});

export const step4UiSchema = {
  aprConfig: {
    'ui:field': 'onlineAprConfig',
  },
  inStoreAprConfig: {
    'ui:field': 'inStoreAprConfig',
  },
};

export const step6UiSchema = {
  validation: { 'ui:field': 'pricingValidator' },
};

export const step7UiSchema = {
  featureConfig: {
    classNames: 'hidden-title',
    globalPricing: {
      classNames: 'hidden-title',
      usePriceOverrides: {
        classNames: 'hidden-title col-md-6',
        [Origin.INSTORE]: {
          'ui:widget': toggleButton,
        },
        [Origin.ONLINE]: {
          'ui:widget': toggleButton,
        },
        [Origin.FORWARDED]: {
          'ui:widget': toggleButton,
        },
      },
      forwardedPriceOverrideExpiration: {
        classNames: 'col-md-2',
      },
    },
  },
};

export const step8UiSchema = {
  displayConfig: {
    classNames: 'hidden-title',
    hideUsedCarListPrice: {
      classNames: 'hidden-title col-md-6',
      'ui:widget': toggleButton,
    },
    showInstoreMSRP: {
      classNames: 'show-instore-msrp col-md-6',
      'ui:widget': toggleButton,
    },
    differentPriceSource: {
      classNames: 'different-price-source col-md-6',
      useDifferentPriceSourceForUnregistered: {
        classNames: 'use-different-price-source-unregistred',
        'ui:widget': toggleButton,
      },
      availableCarsYears: {
        classNames: 'available-cars-years',
      },
    },
    showOnlineMSRP: {
      classNames: 'show-online-msrp col-md-6',
      'ui:widget': toggleButton,
    },
  },
};

export const step9UiSchema = {
  waiveFirstLeasePaymentYearMakeModels: { 'ui:field': 'signAndDrive' },
};

export const step10UiSchema = {
  marketScanConfig: {
    'ui:order': [
      'incentivesEnabled',
      'useLendersFromMSPortal',
      'paymentsEnabled',
      'taxesEnabled',
      'feesEnabled',
      'accountNumber',
      'taxConfigurations',
      'feeConfigurations',
    ],
    paymentsEnabled: {
      'ui:widget': toggleButton,
    },
    taxesEnabled: {
      'ui:widget': toggleButton,
    },
    feesEnabled: {
      'ui:widget': toggleButton,
    },
    incentivesEnabled: {
      'ui:widget': toggleButton,
    },
    useLendersFromMSPortal: {
      'ui:widget': 'radio',
    },
    taxConfigurations: {
      CO: {
        classNames: 'marketscan-state-config-container',
      },
      MO: {
        classNames: 'marketscan-state-config-container',
      },
      IL: {
        classNames: 'marketscan-state-config-container',
      },
    },
    feeConfigurations: {
      NH: {
        classNames: 'marketscan-state-config-container',
      },
    },
  },
};
